/* ⚠️ GENERATED from app/services/reactionRoster.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* 👍 services/reactionRoster.ts — who reacted, and with what.
 *
 * THE QUESTION THIS ANSWERS
 *   "Tapping the pill should show the members who reacted and their reactions,
 *   and let me add or change my own."
 *
 * 🟥 THE PILL HAD NOWHERE TO GO. Its onPress opened the emoji peek — the picker
 *    you use to react — so tapping an existing reaction offered to add another
 *    one and never said who had reacted. Nothing in the app listed reactors.
 *
 * THE DATA
 *   A message carries `reactions: { [emoji]: uid[] }`, and `reactionsAt` /
 *   `reactionsRemovedAt` beside it hold the last-write-wins stamps. This module
 *   turns that map into the two things a sheet draws: the filter tabs across the
 *   top (All · ❤️ 2 · 👍 1) and the rows beneath them.
 *
 * 🟥 ONE REACTION PER PERSON IS THE PRODUCT'S RULE, AND THE MAP CANNOT ENFORCE
 *    IT. `{'❤️': ['u1'], '👍': ['u1']}` is a representable state — a swap whose
 *    removal was lost, or two devices racing — and a roster built straight from
 *    the map would list the same member twice with two different reactions. The
 *    stamps are what break the tie: the later one is the reaction that person
 *    holds. With no stamps at all, the busier bucket wins, which is stable and
 *    does not depend on object key order.
 *
 * 🟢 A BROADCAST HAS COUNTS AND NO NAMES, by construction — each recipient
 *    reacted on their own private copy and the total lives in one public
 *    document (see broadcastReactions.ts). `rosterFor` says so plainly through
 *    `namesKnown: false` rather than drawing an empty list under a count of 12.
 *
 * PURE. No Firestore, no React, no names — the caller resolves a uid to a person
 * because only it knows the chat's participants.
 */

const uidsOf = v => {
  if (Array.isArray(v)) return v.map(u => String(u)).filter(Boolean);
  if (v) return [String(v)];
  return [];
};
const stampOf = (at, emoji, uid) => {
  try {
    const n = Number(at?.[emoji]?.[uid]);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

/**
 * `{emoji: uid[]}` → one row per person.
 *
 * 🟢 THE TIE-BREAK IS THE WHOLE FUNCTION. See the note at the top: a person can
 *    appear under two emoji, and which one they "hold" is decided by the LWW
 *    stamp, then by bucket size, then by the emoji itself — three rules deep so
 *    the answer never depends on the order keys happen to come out in.
 */
export const rosterFor = (message, meId, agg) => {
  const me = meId ? String(meId) : '';

  /* a broadcast: counts without reactors */
  const aggCounts = agg && agg.counts && typeof agg.counts === 'object' ? agg.counts : null;
  const aggTabs = aggCounts ? Object.entries(aggCounts).map(([e, n]) => ({
    emoji: e,
    count: Number(n) || 0
  })).filter(t => t.count > 0).sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji)) : [];
  if (aggTabs.length) {
    const total = aggTabs.reduce((n, t) => n + t.count, 0);
    /* 🟢 the viewer's OWN reaction is still knowable — it is on their own copy */
    const localMine = myReaction(message, me);
    return {
      rows: [],
      tabs: [{
        emoji: null,
        count: total
      }, ...aggTabs],
      total,
      mine: localMine,
      namesKnown: false
    };
  }
  const map = message && typeof message.reactions === 'object' && message.reactions ? message.reactions : {};
  const at = message?.reactionsAt;

  /* 🟢 bucket sizes first, because they are the SECOND tie-break and must be
     known before any row is decided */
  const size = {};
  for (const [emoji, v] of Object.entries(map)) size[emoji] = uidsOf(v).length;
  const best = new Map();
  const bestRank = new Map();
  for (const [emoji, v] of Object.entries(map)) {
    for (const uid of uidsOf(v)) {
      const rank = [stampOf(at, emoji, uid), size[emoji] || 0, emoji];
      const cur = bestRank.get(uid);
      const wins = !cur || rank[0] > cur[0] || rank[0] === cur[0] && rank[1] > cur[1] || rank[0] === cur[0] && rank[1] === cur[1] && rank[2] < cur[2];
      if (wins) {
        bestRank.set(uid, rank);
        best.set(uid, {
          uid,
          emoji,
          isMe: !!me && uid === me
        });
      }
    }
  }
  const rows = Array.from(best.values());
  /* 🟢 the viewer first — it is their own row they look for, and it is the row
     that is tappable (tapping it takes the reaction off) */
  rows.sort((a, b) => a.isMe === b.isMe ? 0 : a.isMe ? -1 : 1);
  const counts = {};
  for (const r of rows) counts[r.emoji] = (counts[r.emoji] || 0) + 1;
  const tabs = Object.entries(counts).map(([e, n]) => ({
    emoji: e,
    count: n
  })).sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji));
  return {
    rows,
    tabs: rows.length ? [{
      emoji: null,
      count: rows.length
    }, ...tabs] : [],
    total: rows.length,
    mine: rows.find(r => r.isMe)?.emoji || null,
    namesKnown: true
  };
};

/**
 * Which reaction does this person hold? Same tie-break as `rosterFor`, so the
 * highlighted emoji in the picker and the viewer's own row can never disagree.
 */
export const myReaction = (message, meId) => {
  const me = meId ? String(meId) : '';
  if (!me) return null;
  const r = rosterFor({
    reactions: message?.reactions,
    reactionsAt: message?.reactionsAt
  }, me).rows.find(x => x.isMe);
  return r ? r.emoji : null;
};

/** The rows a tab shows. `null` is the All tab. */
export const rowsForTab = (roster, tab) => tab == null ? roster.rows : roster.rows.filter(r => r.emoji === tab);

/**
 * What tapping `emoji` in the sheet means.
 *
 * 🟢 TAPPING THE ONE YOU ALREADY HOLD TAKES IT OFF. That is the same gesture the
 *    picker has always had, and it is the only way to "remove my reaction"
 *    without a second control — so the sheet states it here rather than
 *    re-deriving it in the component.
 */
export const reactionTapIntent = (mine, tapped) => mine && mine === tapped ? {
  action: 'remove',
  emoji: tapped
} : {
  action: 'add',
  emoji: tapped
};
export default {
  rosterFor,
  myReaction,
  rowsForTab,
  reactionTapIntent
};
